import { Fragment, useEffect, useState } from "react";
import type { ChangeEntry } from "@spoonity/flags-contract";
import { getDefinitionHistory } from "../../api/endpoints";
import { EmptyState } from "../../components/EmptyState";
import { JsonDiff } from "../../components/JsonDiff";
import { LoadMore } from "../../components/LoadMore";
import { useToast } from "../../components/Toast";

/** Version history from `/changes?flag=` with a before/after JSON diff view (task brief). */
export function HistoryPanel({ environmentId, flagKey }: { environmentId: string; flagKey: string }) {
  const { pushError } = useToast();
  const [entries, setEntries] = useState<ChangeEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setEntries([]);
    setExpanded(null);
    (async () => {
      try {
        const page = await getDefinitionHistory(environmentId, flagKey, { limit: 20 });
        if (cancelled) return;
        setEntries(page.items);
        setNextCursor(page.nextCursor);
      } catch (err) {
        if (!cancelled) pushError(err, "Could not load history");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [environmentId, flagKey, pushError]);

  async function loadMore() {
    if (!nextCursor) return;
    setLoading(true);
    try {
      const page = await getDefinitionHistory(environmentId, flagKey, { cursor: nextCursor, limit: 20 });
      setEntries((cur) => [...cur, ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      pushError(err, "Could not load history");
    } finally {
      setLoading(false);
    }
  }

  if (!loading && entries.length === 0) {
    return <EmptyState title="No history yet" message="Changes to this flag's definition will appear here." />;
  }

  return (
    <div>
      <table className="table">
        <thead>
          <tr>
            <th>When</th>
            <th>Actor</th>
            <th>Action</th>
            <th>Comment</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <Fragment key={entry.id}>
              <tr>
                <td>{new Date(entry.createdAt).toLocaleString()}</td>
                <td>{entry.actor}</td>
                <td>{entry.action}</td>
                <td>{entry.comment ?? <span className="muted">—</span>}</td>
                <td>
                  <button
                    className="link-button"
                    onClick={() => setExpanded((cur) => (cur === entry.id ? null : entry.id))}
                  >
                    {expanded === entry.id ? "Hide diff" : "View diff"}
                  </button>
                </td>
              </tr>
              {expanded === entry.id && (
                <tr>
                  <td colSpan={5}>
                    <JsonDiff before={entry.before} after={entry.after} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
      <LoadMore nextCursor={nextCursor} loading={loading} onLoadMore={loadMore} />
    </div>
  );
}
